import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchFacets } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewImportSpend from '@app/src/components/Drawer/Views/DrawerViewImportSpend'
import EmptyState from '@app/src/components/EmptyState'
import { EmptyStateVariant } from '@app/src/components/EmptyState/EmptyState'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import LoadingButton from '@app/src/components/LoadingButton'
import { spendClassificationColors } from '@app/src/components/Table/Cells/ProviderSpendCell'
import TextField from '@app/src/components/Ui/TextField'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import ConfigurationSkeleton from '@app/src/pages/Configurations/ConfigurationsSkeleton'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { Unit } from '@app/src/types/resourceExplorer'
import { getYears } from '@app/src/utils'
import { CURRENCY_SYMBOLS_FOR_SPEND } from '@app/src/utils/currencyUtils'
import { isNotPositiveWholeNumber } from '@app/src/utils/helpers'
import type { OrganizationSettings } from '@app/src/wf-constants'
import { ONE_MINUTE_MS, SpendClassification, SpendIntervalType, TWELVE_HOURS_MS } from '@app/src/wf-constants'
import { Add, AssistantSharp, EuroOutlined } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, InputAdornment, Stack, Tooltip, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'
import React, { useEffect, useMemo, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

type SettingsChanged = Pick<
  OrganizationSettings,
  'currencyUnitId' | 'mediumSpendMinValue' | 'mediumSpendMaxValue' | 'spendIntervalType' | 'spendPeriodFocus'
>

const UNIT_CACHE_TIME = TWELVE_HOURS_MS
const staleTime = ONE_MINUTE_MS

const spendCircleStyling = {
  height: 12,
  width: 12,
  borderRadius: '100%',
}

const OrganizationPreferencesConfig: React.FC = () => {
  const {
    isFetched: isOrganizationSettingsFetched,
    settings,
    updateOrganizationSettings,
    isSaving,
  } = useOrganizationSettings()
  const confirm = useConfirm()
  const { openDrawer } = useDrawer()
  const providerType = useCurrentProviderType()
  const queryClient = useQueryClient()
  const { formatMessage } = useIntl()
  const minimumMediumSpend = settings?.mediumSpendMinValue
  const maximumMediumSpend = settings?.mediumSpendMaxValue
  const spendIntervalType = settings?.spendIntervalType
  const spendPeriodFocus = settings?.spendPeriodFocus ?? null
  const isAutomaticSpendIntervalType = spendIntervalType === SpendIntervalType.Automatic

  const [isEditing, setIsEditing] = useState(false)
  const currencyUnitId = settings?.currencyUnitId
  const formMethods = useForm<SettingsChanged>()
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    errors,
    clearErrors,
    reset,
    formState: { isDirty },
  } = formMethods

  const { items: units, isFetched: isUnitsFetched } = useFetchCollectionWithPost<Unit>({
    key: FetchKey.Unit,
    endpoint: endpoints.units,
    payload: {
      filter: [{ name: 'symbol', filters: [{ value: CURRENCY_SYMBOLS_FOR_SPEND, operator: Operators.In }] }],
      include: [],
      sort: { target: 'symbol', order: SortOrder.ASCENDING },
    },
    options: { refetchOnMount: false, gcTime: UNIT_CACHE_TIME, staleTime: UNIT_CACHE_TIME },
  })

  useEffect(() => {
    if (isOrganizationSettingsFetched && isUnitsFetched) {
      reset({
        currencyUnitId,
        spendPeriodFocus,
        mediumSpendMinValue: isAutomaticSpendIntervalType ? NaN : Number(minimumMediumSpend),
        mediumSpendMaxValue: isAutomaticSpendIntervalType ? NaN : Number(maximumMediumSpend),
      })
    }
  }, [
    isOrganizationSettingsFetched,
    isUnitsFetched,
    reset,
    currencyUnitId,
    minimumMediumSpend,
    maximumMediumSpend,
    isAutomaticSpendIntervalType,
    spendPeriodFocus,
  ])

  const selectedCurrencyUnitId = useWatch({ control, name: 'currencyUnitId', defaultValue: currencyUnitId })
  const selectedCurrency = units.find(u => u.id === selectedCurrencyUnitId)?.symbol || ''
  const spendPeriodFocusOptions = useMemo(() => {
    const notSelectedOption = {
      value: null,
      label: formatMessage({
        id: 'organizationPreferences.spendPeriodFocusNotSelected',
      }),
    }
    const defaultOptions = getYears(10, 0).map(year => ({
      value: String(year),
      label: String(year),
    }))

    const selectedPeriod = spendPeriodFocus
    const hasSelectedPeriod = selectedPeriod !== null && defaultOptions.some(option => option.value === selectedPeriod)

    if (selectedPeriod === null || hasSelectedPeriod) return [notSelectedOption, ...defaultOptions]

    return [notSelectedOption, { value: selectedPeriod, label: selectedPeriod }, ...defaultOptions]
  }, [spendPeriodFocus, formatMessage])

  const {
    facets: [providersCount = []],
  } = useFetchFacets({
    key: FetchKey.ProviderCount,
    endpoint: endpoints.providersWithFacets,
    facetsParam: [{ name: 'id' }],
    options: {
      enabled: true,
      staleTime: staleTime,
    },
  })

  const {
    facets: [providerSpends = []],
    isFetched: isProviderSpendsFetched,
  } = useFetchFacets({
    key: FetchKey.ProviderFacets,
    endpoint: endpoints.providersWithFacets,
    facetsParam: [{ name: 'spends.amount' }],
    options: {
      enabled: true,
      staleTime: staleTime,
    },
  })

  const maximumProviderSpend = useMemo(() => {
    if (!providerSpends.length) return null
    return Math.max(...providerSpends.map(x => Number(x.label)))
  }, [providerSpends])

  const isProviderSpendAdded = Boolean(providerSpends.length)
  const showAutomaticSpendIntervalIndicatorIcon = !isEditing && isAutomaticSpendIntervalType && isProviderSpendAdded

  const noProvidersEmptyState = !providersCount.length

  const cancelEdit = () => {
    if (isDirty) {
      confirm({
        hideCancelButton: false,
        confirmationText: formatMessage({ id: 'general.confirm' }),
        title: formatMessage({ id: 'general.youHaveUnsavedChanges' }),
        content: formatMessage({ id: 'general.exitWithoutSaving' }),
      }).then(() => setIsEditing(false))
    } else {
      setIsEditing(false)
    }
  }

  const getMinErrorMessage = (minValue: number, maxValue: number, maximumProviderSpend: number | null) => {
    if (Number.isNaN(minValue) && maxValue) {
      return formatMessage({ id: 'error.requiredFields' })
    } else if (isNotPositiveWholeNumber(minValue)) {
      return formatMessage({ id: 'error.mustBePositiveWholeNumber' })
    } else if (minValue && maximumProviderSpend && minValue >= maximumProviderSpend) {
      return formatMessage({ id: 'error.valueHigherThanProviderSpend' }, { providerSpend: maximumProviderSpend })
    }
    return null
  }

  const getMaxErrorMessage = (minValue: number, maxValue: number, maximumProviderSpend: number | null) => {
    if (Number.isNaN(maxValue) && minValue) {
      return formatMessage({ id: 'error.requiredFields' })
    } else if (minValue && maxValue && minValue >= maxValue) {
      return formatMessage({ id: 'error.maxLowerThanMin' })
    } else if (isNotPositiveWholeNumber(maxValue)) {
      return formatMessage({ id: 'error.mustBePositiveWholeNumber' })
    } else if (maxValue && maximumProviderSpend && maxValue >= maximumProviderSpend) {
      return formatMessage({ id: 'error.valueHigherThanProviderSpend' }, { providerSpend: maximumProviderSpend })
    }
    return null
  }

  const submitHandler = async (values: SettingsChanged, skipValidation = false) => {
    const { mediumSpendMinValue, mediumSpendMaxValue, spendPeriodFocus } = values
    const minValue = mediumSpendMinValue === null ? NaN : (Number(mediumSpendMinValue) ?? NaN)
    const maxValue = mediumSpendMaxValue === null ? NaN : (Number(mediumSpendMaxValue) ?? NaN)
    clearErrors()

    if (!skipValidation) {
      const minValueIsFalsyButNotZero = [NaN, null, undefined].includes(mediumSpendMinValue)
      const maxValueIsFalsyButNotZero = [NaN, null, undefined].includes(mediumSpendMaxValue)

      if (!(minValueIsFalsyButNotZero && maxValueIsFalsyButNotZero)) {
        const minErrorMessage = getMinErrorMessage(minValue, maxValue, maximumProviderSpend)
        const maxErrorMessage = getMaxErrorMessage(minValue, maxValue, maximumProviderSpend)

        if (minErrorMessage) {
          setError('mediumSpendMinValue', { message: minErrorMessage })
        }
        if (maxErrorMessage) {
          setError('mediumSpendMaxValue', { message: maxErrorMessage })
        }

        if (minErrorMessage || maxErrorMessage) return
      }
    }

    await updateOrganizationSettings({
      ...values,
      spendPeriodFocus: spendPeriodFocus || null,
      mediumSpendMinValue: skipValidation ? null : values.mediumSpendMinValue,
      mediumSpendMaxValue: skipValidation ? null : values.mediumSpendMaxValue,
    })
    setIsEditing(false)
  }

  const onSubmit: SubmitHandler<SettingsChanged> = async values => {
    await submitHandler(values, false)
  }

  const onSetAutomaticInterval = async () => {
    const currentValues = formMethods.getValues()
    await submitHandler(currentValues, true)
  }

  const getButtons = (): ActionButton[] =>
    isEditing
      ? [
          {
            label: formatMessage({ id: 'general.cancel' }),
            variant: 'text',
            size: 'large',
            onClick: cancelEdit,
            loading: isSaving,
          },
          {
            label: formatMessage({ id: 'general.save' }),
            variant: 'contained',
            size: 'large',
            onClick: handleSubmit(onSubmit),
            loading: isSaving,
          },
        ]
      : [
          {
            label: formatMessage({ id: 'general.edit' }),
            variant: 'contained',
            size: 'large',
            onClick: () => setIsEditing(true),
          },
        ]

  if (!isOrganizationSettingsFetched || !isUnitsFetched || !isProviderSpendsFetched) return <ConfigurationSkeleton />

  const onSpendImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationSettings] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderFacets] })
  }

  return (
    <ConfigurationCollection actionButtons={getButtons()}>
      <FormProvider {...formMethods}>
        <form>
          <Box bgcolor="common.white" mr={4} mb={2} p={2} borderRadius={1}>
            <Grid container>
              <Grid item xs={12} lg={6}>
                <Stack spacing={3}>
                  <Stack>
                    <Typography variant="body2">{formatMessage({ id: 'general.currency' })}</Typography>
                    {isEditing ? (
                      <Box pb={1}>
                        <Select<number>
                          fullWidth
                          hoveringLabel
                          name="currencyUnitId"
                          options={units.map(u => ({ value: u.id, label: u.symbol ?? u.name }))}
                          error={errors?.currencyUnitId?.message}
                          fieldLabel=""
                          control={control}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {units.find(u => u.id === currencyUnitId)?.symbol}
                      </Typography>
                    )}
                  </Stack>
                  <Stack>
                    <Typography variant="body2">
                      {formatMessage({
                        id: 'organizationPreferences.spendPeriodFocus',
                      })}
                    </Typography>
                    {isEditing ? (
                      <Box pb={1}>
                        <Select<string | null>
                          fullWidth
                          hoveringLabel
                          name="spendPeriodFocus"
                          options={spendPeriodFocusOptions}
                          fieldLabel=""
                          control={control}
                          defaultValue={spendPeriodFocus}
                          onInputChange={(e, value) => {
                            if (!value) setValue('spendPeriodFocus', null)
                          }}
                          enableAutoSelect
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {spendPeriodFocus ??
                          formatMessage({
                            id: 'organizationPreferences.spendPeriodFocusNotSelected',
                          })}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              mt: isEditing || !isAutomaticSpendIntervalType ? 4 : 3,
              justifyContent: 'space-between',
              mr: 4,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              {showAutomaticSpendIntervalIndicatorIcon ? (
                <Tooltip
                  arrow
                  title={formatMessage(
                    { id: 'organizationPreferences.groupBySpendTooltip' },
                    { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
                  )}
                >
                  <span>
                    <IconButton sx={{ color: 'communication.dark', cursor: 'default', ml: '-10px' }}>
                      <AssistantSharp />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <Typography variant="subtitle1">
                {formatMessage({ id: 'organizationPreferences.groupBySpend' })}
              </Typography>
            </Stack>

            {isProviderSpendAdded && !isAutomaticSpendIntervalType && (
              <LoadingButton
                startIcon={<AssistantSharp color="primary" />}
                size="small"
                onClick={onSetAutomaticInterval}
                loading={isSaving}
                variant="text"
              >
                {formatMessage({ id: 'organizationPreferences.setAutomaticInterval' })}
              </LoadingButton>
            )}
          </Stack>

          {isProviderSpendAdded ? (
            <Box mt={isEditing || !isAutomaticSpendIntervalType ? 2 : 1} mr={4}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3} lg={3.5}>
                  <Box p={2} bgcolor="common.white" height="100%">
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Box {...spendCircleStyling} bgcolor={spendClassificationColors[SpendClassification.Low]} />
                      <Typography color={isEditing ? 'text.disabled' : 'text.primary'}>
                        {formatMessage({ id: 'organizationPreferences.lowSpend' })}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color={isEditing ? 'text.disabled' : 'text.secondary'}
                      lineHeight="1.4em"
                      pt="3px"
                    >
                      {minimumMediumSpend
                        ? `0 → ${minimumMediumSpend} ${selectedCurrency}`
                        : `0 → ${formatMessage({ id: 'organizationPreferences.noSpendInfo' })}`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={5}>
                  <Box p={2} bgcolor="common.white" height="100%">
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Box {...spendCircleStyling} bgcolor={spendClassificationColors[SpendClassification.Medium]} />
                      <Typography>{formatMessage({ id: 'organizationPreferences.mediumSpend' })}</Typography>
                    </Box>
                    <Stack direction="row" spacing={4}>
                      {isEditing ? (
                        <>
                          <Box width="50%" pb={1}>
                            <Controller
                              name="mediumSpendMinValue"
                              control={control}
                              render={({ onChange, onBlur, value, name }) => (
                                <TextField
                                  fullWidth
                                  label={formatMessage({ id: 'organizationPreferences.min' })}
                                  type="number"
                                  placeholder={formatMessage({ id: 'organizationPreferences.setValue' })}
                                  onChange={e => {
                                    const inputValue = e.target.value
                                    onChange(inputValue ? Number(inputValue) : null)
                                  }}
                                  onBlur={onBlur}
                                  value={value}
                                  name={name}
                                  error={Boolean(errors?.mediumSpendMinValue)}
                                  helperText={errors?.mediumSpendMinValue?.message}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">{selectedCurrency}</InputAdornment>,
                                  }}
                                />
                              )}
                            />
                          </Box>

                          <Box width="50%" pb={1}>
                            <Controller
                              name="mediumSpendMaxValue"
                              control={control}
                              render={({ onChange, onBlur, value, name }) => (
                                <TextField
                                  fullWidth
                                  label={formatMessage({ id: 'organizationPreferences.max' })}
                                  type="number"
                                  placeholder={formatMessage({ id: 'organizationPreferences.setValue' })}
                                  onChange={e => {
                                    const inputValue = e.target.value
                                    onChange(inputValue ? Number(inputValue) : null)
                                  }}
                                  onBlur={onBlur}
                                  value={value}
                                  name={name}
                                  error={Boolean(errors?.mediumSpendMaxValue)}
                                  helperText={errors?.mediumSpendMaxValue?.message}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">{selectedCurrency}</InputAdornment>,
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </>
                      ) : (
                        <Typography color="text.secondary" variant="body2">
                          {minimumMediumSpend && maximumMediumSpend
                            ? `${minimumMediumSpend} ${selectedCurrency} → ${maximumMediumSpend} ${selectedCurrency}`
                            : '-'}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} lg={3.5}>
                  <Box p={2} bgcolor="common.white" height="100%">
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Box {...spendCircleStyling} bgcolor={spendClassificationColors[SpendClassification.High]} />
                      <Typography color={isEditing ? 'text.disabled' : 'text.primary'}>
                        {formatMessage({ id: 'organizationPreferences.highSpend' })}
                      </Typography>
                    </Box>
                    <Typography
                      color={isEditing ? 'text.disabled' : 'text.secondary'}
                      variant="body2"
                      lineHeight="1.4em"
                      pt="3px"
                    >
                      {(maximumMediumSpend
                        ? `${maximumMediumSpend} ${selectedCurrency}`
                        : `${formatMessage({ id: 'organizationPreferences.noSpendInfo' })}`) +
                        (maximumMediumSpend || maximumProviderSpend ? ' → ' : '') +
                        (maximumProviderSpend ? `${maximumProviderSpend} ${selectedCurrency}` : '')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box px={4} py={5} mt={1} mr={4} borderRadius={1} sx={{ backgroundColor: 'grey.200' }}>
              <EmptyState
                iconComponent={EuroOutlined}
                title={formatMessage({ id: 'organizationPreferences.groupBySpendEmptyState.description' })}
                variant={EmptyStateVariant.Small}
              >
                <Tooltip
                  arrow
                  title={
                    noProvidersEmptyState ? formatMessage({ id: 'import.noProvidersEmptyState' }, { providerType }) : ''
                  }
                >
                  <Box>
                    <Button
                      disabled={noProvidersEmptyState}
                      startIcon={<Add />}
                      variant="contained"
                      size="small"
                      sx={{ mb: 1.5 }}
                      onClick={() =>
                        openDrawer(
                          <DrawerViewImportSpend
                            providerType={providerType}
                            allowedFilters={[]}
                            onImportSuccess={onSpendImportSuccess}
                          />,
                        )
                      }
                    >
                      {formatMessage({ id: 'organizationPreferences.groupBySpendEmptyState.button' })}
                    </Button>
                  </Box>
                </Tooltip>
              </EmptyState>
            </Box>
          )}
        </form>
      </FormProvider>
    </ConfigurationCollection>
  )
}

export default OrganizationPreferencesConfig
